// Turf-based intersection maths for zone land data.
//
// All area fractions are fractions of the ZONE's area (not of the fetched
// features). Degenerate or invalid geometries are skipped per-feature rather
// than failing the whole computation.
//
// Overlapping features (e.g. a flood-zone FZ2 polygon that contains an FZ3
// polygon) would double-count if we simply summed each feature's intersection
// area with the zone. To avoid that, every dataset feature is first clipped to
// the zone and the clipped pieces are DISSOLVED with a polygon union before we
// measure area — so any shared overlap is counted once. The Math.min(..., 1)
// clamps are kept only as a defensive backstop.

import { area } from "@turf/area";
import { intersect } from "@turf/intersect";
import { union } from "@turf/union";

function asFeature(geometryOrFeature) {
  if (geometryOrFeature.type === "Feature") return geometryOrFeature;
  return { type: "Feature", properties: {}, geometry: geometryOrFeature };
}

/**
 * Clip one dataset feature to the zone, returning the clipped GeoJSON Feature
 * (Polygon/MultiPolygon) or null when they don't overlap or the intersection
 * is degenerate. Invalid geometry is swallowed and treated as no overlap.
 */
function clipToZone(zoneFeature, feature) {
  try {
    const overlap = intersect({
      type: "FeatureCollection",
      features: [zoneFeature, asFeature(feature)],
    });
    return overlap || null;
  } catch (err) {
    // Invalid/degenerate geometry — skip this feature.
    return null;
  }
}

/**
 * Dissolve a list of clipped polygon Features into a single geometry and return
 * its area (m²). Unioning removes any overlap between the pieces so shared area
 * is counted once. Unions incrementally and skips any piece that fails to merge
 * (rather than letting one bad geometry crash the whole computation).
 */
function dissolvedArea(clippedFeatures) {
  let merged = null;
  for (const piece of clippedFeatures) {
    if (!piece) continue;
    if (!merged) {
      merged = piece;
      continue;
    }
    try {
      const next = union({
        type: "FeatureCollection",
        features: [merged, piece],
      });
      // union returns null only when there was nothing to combine; keep the
      // running accumulator if that (shouldn't) happen.
      if (next) merged = next;
    } catch (err) {
      // This piece won't union cleanly — skip it, keep what we have so far.
    }
  }
  if (!merged) return 0;
  try {
    return area(merged);
  } catch (err) {
    return 0;
  }
}

/**
 * Per-class area breakdown of a zone against a graded layer's features.
 * Features sharing a class code are merged. Fractions are of the zone's area.
 *
 * @param {object} zonePolygon GeoJSON Polygon geometry of the zone (WGS84)
 * @param {Array} features GeoJSON Features from the layer
 * @param {string} classCodeField attribute holding the class code
 * @param {string} [classNameField] optional attribute holding a display name
 * @param {string} [classLinkField] optional attribute holding a per-class link
 *   value (e.g. Soilscapes SS_ID) used to build a "learn more" deep link
 * @param {string} [legendClassField] optional attribute holding the value the
 *   MAP is styled on, which is not always the field the panel breaks down by.
 *   Scotland's soil panel groups by soil association (ASSOC) while the map
 *   paints 11 generalised types (genSoilTyp), so a legend keyed on the panel's
 *   field would list classes the map does not colour apart. Produces
 *   `legendClasses`, which is only ever used to build the map legend.
 * @returns {{ classes: Array<{code: string, name: string|null, link: string|null, areaFraction: number}>,
 *             legendClasses: Array<{value: string, areaFraction: number}>,
 *             coveredFraction: number, distinctClassCount: number }}
 */
export function computeGradedBreakdown(
  zonePolygon,
  features,
  classCodeField,
  classNameField,
  classLinkField,
  legendClassField
) {
  const zoneFeature = asFeature(zonePolygon);
  const zoneArea = area(zoneFeature);
  const byCode = new Map();
  const byLegendValue = new Map();
  const allClipped = [];

  for (const feature of features || []) {
    const clipped = clipToZone(zoneFeature, feature);
    if (!clipped) continue;

    const props = feature.properties || {};

    // Grouped in the same pass as the panel's breakdown so the clipping maths
    // is done once, not twice.
    if (legendClassField) {
      const rawLegendValue = props[legendClassField];
      if (rawLegendValue !== null && rawLegendValue !== undefined) {
        const legendValue = String(rawLegendValue);
        const pieces = byLegendValue.get(legendValue) || [];
        pieces.push(clipped);
        byLegendValue.set(legendValue, pieces);
      }
    }

    const rawCode = props[classCodeField];
    const code = rawCode === null || rawCode === undefined ? "unknown" : String(rawCode);
    const name =
      classNameField && props[classNameField] !== undefined && props[classNameField] !== null
        ? String(props[classNameField])
        : null;
    const link =
      classLinkField && props[classLinkField] !== undefined && props[classLinkField] !== null
        ? String(props[classLinkField])
        : null;

    const entry = byCode.get(code) || { code, name, link, pieces: [] };
    entry.pieces.push(clipped);
    if (!entry.name && name) entry.name = name;
    if (!entry.link && link) entry.link = link;
    byCode.set(code, entry);
    allClipped.push(clipped);
  }

  // Per-class area is the union of that code's clipped pieces (features sharing
  // a class code that overlap each other are counted once).
  const classes = Array.from(byCode.values())
    .map(({ code, name, link, pieces }) => {
      const areaM2 = dissolvedArea(pieces);
      return {
        code,
        name,
        link: link || null,
        areaFraction: zoneArea > 0 ? Math.min(areaM2 / zoneArea, 1) : 0,
      };
    })
    .sort((a, b) => b.areaFraction - a.areaFraction);

  // Covered area is the union of ALL clipped pieces across every class, so
  // overlaps between different classes aren't double-counted either.
  const coveredArea = dissolvedArea(allClipped);

  const legendClasses = Array.from(byLegendValue.entries())
    .map(([value, pieces]) => ({
      value,
      areaFraction: zoneArea > 0 ? Math.min(dissolvedArea(pieces) / zoneArea, 1) : 0,
    }))
    .sort((a, b) => b.areaFraction - a.areaFraction);

  return {
    classes,
    legendClasses,
    coveredFraction: zoneArea > 0 ? Math.min(coveredArea / zoneArea, 1) : 0,
    distinctClassCount: classes.length,
  };
}

/**
 * Fraction of the zone's area covered by any of the given features (0..1).
 * Used for categorical layers (designations, flood zones, NVZs).
 */
export function computeCoveredFraction(zonePolygon, features) {
  const zoneFeature = asFeature(zonePolygon);
  const zoneArea = area(zoneFeature);
  if (zoneArea <= 0) return 0;

  // Clip every feature to the zone, then dissolve the clipped pieces with a
  // union before measuring: overlapping features (e.g. FZ2 and FZ3) are counted
  // once instead of summed. The clamp below is a defensive backstop only.
  const clipped = [];
  for (const feature of features || []) {
    const piece = clipToZone(zoneFeature, feature);
    if (piece) clipped.push(piece);
  }

  const coveredArea = dissolvedArea(clipped);
  return Math.min(coveredArea / zoneArea, 1);
}
